/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/array/ArraySplit.ts
 * @create:      2025-06-26 15:59:16.613
 * @modify:      2025-07-11 21:19:12.782
 * @version:     6.0.0
 * @times:       6
 * @lines:       73
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Splits an array into chunks of a specified size.
 *
 * @param array - The array to split into chunks.
 * @param size - The maximum size of each chunk.
 * @param fromLeft - If `true`, splits the array from left to right. If `false` or omitted, splits from right to left, placing the smaller chunk (if any) at the start.
 * @returns An array of arrays, where each sub-array is a chunk of the original array.
 *
 * @example
 * ArraySplit([1, 2, 3, 4, 5], 2, true); // [[1, 2], [3, 4], [5]]
 * ArraySplit([1, 2, 3, 4, 5], 2); // [[1], [2, 3], [4, 5]]
 * 
 * @since 4.0.0
 * @version 2025-06-27
 */
export function ArraySplit<T>(array: T[], size: number, fromLeft?: boolean) {
    if (size <= 0) {
        throw new Error(`Invalid size: ${size}. Size must be greater than 0.`);
    }

    const result: T[][] = new Array(0);

    if (fromLeft === true) {
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
    } else {
        const remainder = array.length % size;

        if (remainder > 0) {
            result.push(array.slice(0, remainder));
        }

        for (let i = remainder; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
    }

    return result;
}
